// features/playbook-create-chat.js — CREATE WITH SHIFT
// ═══════════════════════════════════════════════════════════
// Chat-first creation surface for the Playbook. Split-screen:
// conversation on the left, live preview card on the right.
// Starter chips above the input solve the blank-page problem.
// Shift classifies + structures; user just types their thought.
//
// Flow:
//   1. Opening — question + 6 starter chips
//   2. User types, Shift responds (follow-up OR proposal)
//   3. Proposal → live preview card renders (editable inline)
//   4. Save → POST /api/playbook create + close overlay
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { STARTER_CHIPS } from '../lib/playbook-schemas';
import PlaybookPreviewCard from '../components/playbook-preview-card';

export default function PlaybookCreateChat({ userId, supabase, userRecord, onClose, onSaved, onManual }) {
  const aiName = userRecord?.ai_name || 'Shift';

  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'What are you adding to your playbook?' },
  ]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  async function authHeaders() {
    const { data: { session } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` };
  }

  async function send(text) {
    const body = text.trim();
    if (!body || pending) return;
    const nextMessages = [...messages, { role: 'user', content: body }];
    setMessages(nextMessages);
    setInput('');
    setPending(true);
    setError(null);

    try {
      const headers = await authHeaders();
      const res = await fetch('/api/chat', {
        method: 'POST', headers,
        body: JSON.stringify({
          action: 'playbookCreate',
          aiName,
          messages: nextMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();

      if (data.needsFollowUp) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.question || 'Could you say more?' }]);
      } else if (data.proposal) {
        setProposal(data.proposal);
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: data.acknowledgement || 'Got it. Take a look — edit anything on the card.' },
        ]);
      }
    } catch (e) {
      console.error('PlaybookCreateChat send:', e);
      setError(e.message || 'Something went wrong');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Try once more?' }]);
    } finally {
      setPending(false);
    }
  }

  function handleChipClick(chip) {
    setInput(chip.seed);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (inputRef.current) {
        const v = inputRef.current.value;
        inputRef.current.setSelectionRange(v.length, v.length);
      }
    });
  }

  async function handleSave() {
    if (!proposal || saving) return;
    setSaving(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch('/api/playbook', {
        method: 'POST', headers,
        body: JSON.stringify({
          action: 'create',
          userId,
          itemType: proposal.item_type,
          title: proposal.title,
          content: proposal.content,
          contextTags: proposal.tags || [],
        }),
      });
      if (!res.ok) {
        const { error: errMsg } = await res.json().catch(() => ({}));
        throw new Error(errMsg || 'Save failed');
      }
      const { item } = await res.json();
      onSaved?.(item);
      onClose?.();
    } catch (e) {
      console.error('PlaybookCreateChat save:', e);
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const canSave = !!proposal && !!proposal.title && !saving;

  return (
    <div style={overlayStyle}>
      <div style={headerStyle}>
        <div style={{ fontSize: 15, color: '#c8a96e', fontFamily: 'Georgia, serif', fontWeight: 'bold', letterSpacing: '0.02em' }}>
          Add to Playbook
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {onManual && (
            <button
              onClick={onManual}
              style={{
                background: 'none', border: 'none', color: '#666',
                fontSize: 11, fontFamily: 'Georgia, serif', cursor: 'pointer',
                fontStyle: 'italic', letterSpacing: '0.04em', padding: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#aaa'}
              onMouseLeave={e => e.currentTarget.style.color = '#666'}
            >
              Manual entry
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: '#666',
              fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
            onMouseLeave={e => e.currentTarget.style.color = '#666'}
          >×</button>
        </div>
      </div>

      <div style={splitStyle}>
        {/* LEFT — CHAT */}
        <div style={leftStyle}>
          <div ref={scrollRef} style={chatScrollStyle}>
            {messages.map((m, i) => (
              <div key={i} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 9, color: m.role === 'user' ? '#888' : '#c8a96e', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4, fontFamily: 'Georgia, serif' }}>
                  {m.role === 'user' ? (userRecord?.first_name || 'You') : aiName}
                </div>
                <div style={{ fontSize: 14, color: '#e4e4e4', fontFamily: 'Georgia, serif', lineHeight: 1.7 }}>
                  {m.content}
                </div>
              </div>
            ))}
            {pending && (
              <div style={{ fontSize: 11, color: '#555', fontStyle: 'italic', fontFamily: 'Georgia, serif', marginBottom: 18 }}>
                {aiName} is thinking…
              </div>
            )}
            {error && (
              <div style={{ fontSize: 11, color: '#c46050', fontFamily: 'Georgia, serif', marginBottom: 18 }}>
                {error}
              </div>
            )}
          </div>

          {/* Starter chips — only on opening turn */}
          {messages.length === 1 && !pending && (
            <div style={chipRowStyle}>
              {STARTER_CHIPS.map(chip => (
                <button
                  key={chip.label}
                  onClick={() => handleChipClick(chip)}
                  style={chipStyle}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#c8a96e66'; e.currentTarget.style.color = '#c8a96e'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#aaa'; }}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={inputRowStyle}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
                if (e.key === 'Escape') onClose?.();
              }}
              placeholder="Type what you're capturing…"
              disabled={pending}
              style={inputStyle}
            />
            <button
              onClick={() => send(input)}
              disabled={pending || !input.trim()}
              style={sendBtnStyle(input.trim() && !pending)}
            >
              Send
            </button>
          </div>
        </div>

        {/* RIGHT — PREVIEW + SAVE */}
        <div style={rightStyle}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ maxWidth: 360, margin: '0 auto', width: '100%' }}>
              <PlaybookPreviewCard
                item={proposal}
                onChange={setProposal}
                pending={pending && !proposal}
              />
            </div>
          </div>
          <div style={footerStyle}>
            <button
              onClick={onClose}
              onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
              onMouseLeave={e => e.currentTarget.style.color = '#666'}
              style={{
                background: 'none', border: '1px solid #272736',
                borderRadius: 4, color: '#666',
                fontSize: 12, fontFamily: 'Georgia, serif',
                padding: '7px 18px', cursor: 'pointer',
                transition: 'color 0.15s',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                background: canSave ? 'rgba(200,169,110,0.1)' : 'none',
                border: `1px solid ${canSave ? '#c8a96e' : '#2a2a2a'}`,
                borderRadius: 4,
                color: canSave ? '#c8a96e' : '#444',
                fontSize: 12, fontFamily: 'Georgia, serif', letterSpacing: '0.04em',
                padding: '7px 22px', cursor: canSave ? 'pointer' : 'default',
                transition: 'all 0.15s',
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────
const overlayStyle = {
  position: 'fixed', top: 0, right: 0, bottom: 0,
  left: 'var(--sidebar-width, 300px)',
  background: '#07070a',
  zIndex: 110,
  display: 'flex', flexDirection: 'column',
  fontFamily: 'Georgia, serif',
};

const headerStyle = {
  padding: '14px 28px',
  borderBottom: '1px solid #1e1e2a',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  flexShrink: 0,
};

const splitStyle = {
  flex: 1, display: 'flex', minHeight: 0,
};

const leftStyle = {
  flex: '0 0 55%', display: 'flex', flexDirection: 'column',
  borderRight: '1px solid #1e1e2a', minHeight: 0,
};

const rightStyle = {
  flex: '1 1 45%', display: 'flex', flexDirection: 'column', minHeight: 0,
  background: '#07070a',
};

const chatScrollStyle = {
  flex: 1, overflowY: 'auto',
  padding: '24px 28px 12px',
  minHeight: 0,
};

const chipRowStyle = {
  display: 'flex', flexWrap: 'wrap', gap: 8,
  padding: '0 28px 12px',
  flexShrink: 0,
};

const chipStyle = {
  background: 'transparent', border: '1px solid #2a2a2a',
  borderRadius: 20, color: '#aaa',
  fontSize: 11, fontFamily: 'Georgia, serif',
  padding: '5px 12px', cursor: 'pointer',
  transition: 'all 0.15s',
};

const inputRowStyle = {
  padding: '14px 28px 24px',
  display: 'flex', gap: 10,
  borderTop: '1px solid #141414',
  flexShrink: 0,
};

const inputStyle = {
  flex: 1,
  background: '#141414',
  border: '1px solid #2a2a2a',
  borderRadius: 6,
  color: '#e4e4e4',
  fontSize: 13, fontFamily: 'Georgia, serif',
  padding: '10px 14px',
  outline: 'none',
};

function sendBtnStyle(active) {
  return {
    background: active ? 'rgba(200,169,110,0.12)' : 'none',
    border: `1px solid ${active ? '#c8a96e' : '#2a2a2a'}`,
    borderRadius: 6,
    color: active ? '#c8a96e' : '#444',
    fontSize: 12, fontFamily: 'Georgia, serif',
    padding: '10px 20px', cursor: active ? 'pointer' : 'default',
    transition: 'all 0.15s',
  };
}

const footerStyle = {
  padding: '14px 28px',
  borderTop: '1px solid #1e1e2a',
  display: 'flex', justifyContent: 'flex-end', gap: 10,
  flexShrink: 0,
};
